import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  Cell
} from 'recharts';
import '../Experiments.css';

function BusinessPlanCharts() {
  // 가격 데이터
  const prices = [
    { plan: '베이직', price: 60 },
    { plan: '스탠다드', price: 240 },
    { plan: '프리미엄', price: 1200 },
    { plan: '마이그레이션', price: 20 }
  ];

  // 가격 맵 생성
  const priceMap = {
    베이직: 60,
    스탠다드: 240,
    프리미엄: 1200,
    마이그레이션: 20
  };

  // 고객 수 및 매출 데이터
  const customerData = [
    {
      year: '1년차',
      베이직: 12,
      스탠다드: 6,
      프리미엄: 2,
      마이그레이션: 15,
      매출: 4860
    },
    {
      year: '2년차',
      베이직: 25,
      스탠다드: 18,
      프리미엄: 7,
      마이그레이션: 25,
      매출: 14720
    },
    {
      year: '3년차',
      베이직: 40,
      스탠다드: 40,
      프리미엄: 20,
      마이그레이션: 40,
      매출: 36800
    }
  ];

  // 플랜별 매출액 계산 (누적 막대용)
  const revenueByPlan = customerData.map(item => ({
    year: item.year,
    '베이직 매출': item.베이직 * priceMap.베이직,
    '스탠다드 매출': item.스탠다드 * priceMap.스탠다드,
    '프리미엄 매출': item.프리미엄 * priceMap.프리미엄,
    '마이그레이션 매출': item.마이그레이션 * priceMap.마이그레이션
  }));

  // TAM/SAM/SOM 데이터 (단위: 원) - 반원 차트용
  const marketData = [
    {
      name: 'TAM',
      value: 100, // 최대값을 100%로 설정
      actualValue: 250000000000, // 2.5조 원
      설명: '노무 전문 시장(2조 원) + 중소기업 HR 테크 시장(5,000억 원) 전체',
      innerRadius: 0,
      outerRadius: 100
    },
    {
      name: 'SAM',
      value: 24, // TAM 대비 비율 (6000억 / 25000억 = 24%)
      actualValue: 60000000000, // 6,000억 원
      설명: '중소 노무법인 3,000개사 × 연평균 2,000만 원 + 중소기업 직접 타겟 시장',
      innerRadius: 0,
      outerRadius: 80
    },
    {
      name: 'SOM',
      value: 0.72, // TAM 대비 비율 (18억 / 25000억 = 0.072%)
      actualValue: 1800000000, // 18억 원 (3년 누적)
      설명: '초기 3년간 노무법인 50개사 확보 목표 기준 (연평균 1,200만 원 × 50개사)',
      innerRadius: 0,
      outerRadius: 60
    }
  ];

  // 큰 숫자 포맷팅 함수 (조, 억 단위)
  const formatLargeNumber = (value) => {
    if (value >= 100000000000) {
      return `${(value / 100000000000).toFixed(1)}조`;
    } else if (value >= 100000000) {
      return `${(value / 100000000).toFixed(0)}억`;
    } else if (value >= 10000) {
      return `${(value / 10000).toFixed(0)}만`;
    }
    return formatNumber(value);
  };

  // 시장 규모 차트용 색상
  const marketColors = {
    'TAM': '#8884d8',
    'SAM': '#82ca9d',
    'SOM': '#ffc658'
  };

  // 숫자 포맷팅 함수 (콤마 추가)
  const formatNumber = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 가격 포맷팅용 커스텀 툴팁
  const PriceTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p>{`${payload[0].name}: ${formatNumber(payload[0].value)}`}</p>
        </div>
      );
    }
    return null;
  };

  // 매출 포맷팅용 커스텀 툴팁
  const RevenueTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${formatNumber(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="experiments-container">
      <div className="experiments-grid">
        <div className="experiments-section">
          <h3>플랜별 가격</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={prices}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="plan" />
              <YAxis tickFormatter={formatNumber} />
              <Tooltip content={<PriceTooltip />} />
              <Legend />
              <Bar dataKey="price" fill="#8884d8" name="가격" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="experiments-section">
          <h3>연도별 플랜 고객 수</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={customerData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatNumber} />
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend />
              <Bar dataKey="베이직" fill="#8884d8" name="베이직" />
              <Bar dataKey="스탠다드" fill="#82ca9d" name="스탠다드" />
              <Bar dataKey="프리미엄" fill="#ffc658" name="프리미엄" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="experiments-section">
          <h3>연도별 플랜별 매출액</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenueByPlan}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatNumber} />
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend />
              <Bar dataKey="베이직 매출" stackId="a" fill="#8884d8" name="베이직 매출" />
              <Bar dataKey="스탠다드 매출" stackId="a" fill="#82ca9d" name="스탠다드 매출" />
              <Bar dataKey="프리미엄 매출" stackId="a" fill="#ffc658" name="프리미엄 매출" />
              <Bar dataKey="마이그레이션 매출" stackId="a" fill="#ff7300" name="마이그레이션 매출" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="experiments-section">
          <h3>연도별 고객 수 및 매출 통합</h3>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={customerData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis yAxisId="left" tickFormatter={formatNumber} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={formatNumber} />
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend />
              <Bar yAxisId="left" dataKey="베이직" fill="#8884d8" name="베이직 고객 수" />
              <Bar yAxisId="left" dataKey="스탠다드" fill="#82ca9d" name="스탠다드 고객 수" />
              <Bar yAxisId="left" dataKey="프리미엄" fill="#ffc658" name="프리미엄 고객 수" />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="매출" 
                stroke="#ff0000" 
                strokeWidth={3}
                name="매출"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="experiments-section-full">
        <h3>시장 규모 분석 (TAM/SAM/SOM)</h3>
        <div className="market-chart-container">
          <div className="market-radial-chart-wrapper">
            {marketData.map((entry, index) => {
              // 각 항목을 다른 반경으로 설정 (겹치도록)
              const innerRadius = `${20 + index * 20}%`;
              const outerRadius = `${40 + index * 20}%`;
              return (
                <div key={entry.name} className="market-radial-layer">
                  <ResponsiveContainer width="100%" height={400}>
                    <RadialBarChart
                      cx="50%"
                      cy="80%"
                      innerRadius={innerRadius}
                      outerRadius={outerRadius}
                      barSize={80}
                      data={[entry]}
                      startAngle={180}
                      endAngle={0}
                    >
                      <RadialBar
                        minAngle={15}
                        label={{
                          position: 'insideStart',
                          fill: '#fff',
                          fontSize: 14,
                          fontWeight: 'bold',
                          formatter: () => {
                            return `${entry.name}\n${formatLargeNumber(entry.actualValue)}`;
                          }
                        }}
                        background={{ fill: '#f0f0f0', opacity: 0.2 }}
                        dataKey="value"
                        fill={marketColors[entry.name]}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        </div>
        <div className="market-description">
          {marketData.map((item, index) => (
            <div key={index} className="market-item">
              <div className="market-item-header">
                <span className="market-color-indicator" style={{ backgroundColor: marketColors[item.name] }}></span>
                <strong>{item.name}:</strong> {formatLargeNumber(item.actualValue)}
              </div>
              <div className="market-item-desc">{item.설명}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BusinessPlanCharts;

